import { create } from 'zustand';
import { http } from '@/libs/http';
import { toast } from 'sonner';

export interface VariableDef {
    key: string;
    label?: string;
    default_value?: string;
    description?: string;
    required?: boolean;
}

export interface EmailTemplate {
    id: string;
    key: string;
    name: string;
    description?: string;
    subject: string;
    html_content: string;
    variables: VariableDef[];
    tags: string[];
    event_ids: string[];
    trigger_type: 'http' | 'event';
    service_config_id?: string | null;
    service_config?: any;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface EmailServiceConfig {
    id: string;
    name: string;
    service_type: string;
    http_method: string;
    subject_field_name: string;
    body_field_name: string;
    attachment_field_name?: string | null;
    is_default: boolean;
    is_active: boolean;
    url?: string | null;
}

interface EmailTemplateState {
    templates: EmailTemplate[];
    serviceConfigs: EmailServiceConfig[];
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchTemplates: () => Promise<EmailTemplate[]>;
    fetchServiceConfigs: () => Promise<EmailServiceConfig[]>;
    createTemplate: (data: Partial<EmailTemplate>) => Promise<EmailTemplate>;
    updateTemplate: (idOrKey: string, data: Partial<EmailTemplate>) => Promise<EmailTemplate>;
    deleteTemplate: (idOrKey: string) => Promise<void>;
    cloneTemplate: (idOrKey: string, name: string, key?: string) => Promise<EmailTemplate>;
    sendTestEmail: (templateId: string, to: string | string[], variables: Record<string, any>, serviceConfigId?: string, subject?: string, htmlContent?: string) => Promise<any>;
    renderPreview: (subject: string, htmlContent: string, variablesDef: VariableDef[], payload: Record<string, any>) => Promise<{ subject: string; html_content: string }>;
}

export const useEmailTemplates = create<EmailTemplateState>((set, get) => ({
    templates: [],
    serviceConfigs: [],
    isLoading: false,
    error: null,

    fetchTemplates: async () => {
        set({ isLoading: true, error: null });
        try {
            const data = await http.get<EmailTemplate[]>('/mgmt/v1/email-templates');
            set({ templates: data, isLoading: false });
            return data;
        } catch (err: any) {
            const msg = err.message || 'Failed to fetch email templates';
            set({ error: msg, isLoading: false });
            toast.error(msg);
            return [];
        }
    },

    fetchServiceConfigs: async () => {
        try {
            const data = await http.get<EmailServiceConfig[]>('/mgmt/v1/configurations/email-service');
            set({ serviceConfigs: data });
            return data;
        } catch (err: any) {
            console.error('Failed to fetch email service configs', err);
            return [];
        }
    },

    createTemplate: async (data) => {
        try {
            const newT = await http.post<EmailTemplate>('/mgmt/v1/email-templates', data);
            set({ templates: [newT, ...get().templates] });
            toast.success('Email template created successfully');
            return newT;
        } catch (err: any) {
            const msg = err.message || 'Failed to create email template';
            toast.error(msg);
            throw err;
        }
    },

    updateTemplate: async (idOrKey, data) => {
        try {
            const updated = await http.put<EmailTemplate>(`/mgmt/v1/email-templates/${idOrKey}`, data);
            set({
                templates: get().templates.map(t => (t.id === idOrKey || t.key === idOrKey) ? updated : t)
            });
            toast.success('Email template updated successfully');
            return updated;
        } catch (err: any) {
            const msg = err.message || 'Failed to update email template';
            toast.error(msg);
            throw err;
        }
    },

    deleteTemplate: async (idOrKey) => {
        try {
            await http.delete(`/mgmt/v1/email-templates/${idOrKey}`);
            set({
                templates: get().templates.filter(t => t.id !== idOrKey && t.key !== idOrKey)
            });
            toast.success('Email template deleted successfully');
        } catch (err: any) {
            const msg = err.message || 'Failed to delete template';
            toast.error(msg);
            throw err;
        }
    },

    cloneTemplate: async (idOrKey, name, key) => {
        try {
            const cloned = await http.post<EmailTemplate>(`/mgmt/v1/email-templates/${idOrKey}/clone`, { name, key });
            set({ templates: [cloned, ...get().templates] });
            toast.success('Email template cloned successfully');
            return cloned;
        } catch (err: any) {
            const msg = err.message || 'Failed to clone email template';
            toast.error(msg);
            throw err;
        }
    },

    sendTestEmail: async (templateId, to, variables, serviceConfigId, subject, htmlContent) => {
        try {
            const finalVariables = {
                ...variables,
                to: Array.isArray(to) ? to.join(', ') : to,
                subject: subject || variables.subject || ''
            };
            const result = await http.post(`/mgmt/v1/email-templates/${templateId}/test`, {
                template: templateId,
                body: htmlContent || undefined,
                variables: finalVariables,
                service_config_id: serviceConfigId || undefined
            });
            if (result.status === 'success') {
                toast.success(`Test email sent via ${result.service_used}`);
            } else {
                toast.error(`Email dispatch failed: ${result.response}`);
            }
            return result;
        } catch (err: any) {
            const msg = err.message || 'Failed to send test email';
            toast.error(msg);
            throw err;
        }
    },

    renderPreview: async (subject, htmlContent, variablesDef, payload) => {
        try {
            return await http.post('/mgmt/v1/email-templates/preview', {
                subject,
                html_content: htmlContent,
                variables_def: variablesDef,
                payload
            });
        } catch (err) {
            console.error('Failed to render preview', err);
            return { subject, html_content: htmlContent };
        }
    }
}));
